import { create } from 'zustand';
import { PaginationType } from '../types/pagination';
import {
  deleteEmployees,
  EmployeeType,
  fetchEmployeeById,
  fetchEmployees,
  FetchEmployeesRequest,
  postEmployees,
  putEmployees,
  restoreEmployee,
  unlinkEmployees,
} from './api/employees-api';
import { useAlertStore } from './use-alert-store';

interface Store {
  staff?: EmployeeType[];
  employee?: Partial<EmployeeType>;
  fetchEmployees: (req: FetchEmployeesRequest) => Promise<EmployeeType[] | undefined>;
  fetchEmployeeById: (id: string) => Promise<EmployeeType | undefined>;
  postEmployee: (req: Partial<EmployeeType>) => Promise<void>;
  putEmployee: (req: Partial<EmployeeType>) => Promise<void>;
  deleteEmployee: (id: string) => Promise<void>;
  restoreEmployee: (id: string) => Promise<void>;
  unlinkEmployee: (id: string) => Promise<void>;
  isLoading?: boolean;
  isLoadingFetch?: boolean;
  pagination?: PaginationType;
}

export const useEmployeesStore = create<Store>((set, get) => ({
  fetchEmployees: async (req) => {
    set({ isLoadingFetch: true });
    try {
      const { staff, ...other } = await fetchEmployees(req);

      const isInfinite = !!req.infinite;

      const updatedStaff = isInfinite ? get().staff?.concat(staff) : staff;

      set({ staff: updatedStaff, pagination: { ...other } });
      return updatedStaff;
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по сотрудникам', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  fetchEmployeeById: async (id) => {
    set({ isLoadingFetch: true });
    try {
      const resp = await fetchEmployeeById(id);

      set({ employee: resp });
      return resp;
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по сотрудникам', variant: 'danger' });
      return undefined;
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  postEmployee: async (req) => {
    set({ isLoading: true });
    try {
      const resp = await postEmployees(req);

      useAlertStore.getState().add({ label: 'Сотрудник успешно добавлен', variant: 'success' });
      set((store) => ({ ...store, staff: [resp, ...(store.staff || [])] }));
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по сотруднику', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  putEmployee: async (req) => {
    set({ isLoading: true });
    try {
      const resp = await putEmployees(req);

      set((store) => ({
        ...store,
        staff: store.staff?.map((item) => (item.id === resp.id ? resp : item)),
        employee: resp,
      }));
      useAlertStore.getState().add({ label: 'Данные сотрудника изменены', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по сотруднику', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  deleteEmployee: async (id) => {
    set({ isLoading: true });
    try {
      await deleteEmployees(id);
      useAlertStore.getState().add({ label: 'Сотрудник удален', variant: 'success' });
      set((store) => ({
        ...store,
        staff: store.staff?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: true } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось удалить сотрудника', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restoreEmployee: async (id) => {
    set({ isLoading: true });
    try {
      await restoreEmployee(id);
      useAlertStore.getState().add({ label: 'Сотрудник восстановлен', variant: 'success' });
      set((store) => ({
        ...store,
        staff: store.staff?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: false } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось восстановить сотрудника', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  unlinkEmployee: async (req) => {
    set({ isLoading: true });
    try {
      await unlinkEmployees(req);
      useAlertStore
        .getState()
        .add({ label: 'Сотрудник успешно разавторизован', variant: 'success' });
      set((store) => ({
        ...store,
        staff: store.staff?.map((item) =>
          String(item.id) === req ? { ...item, online: false } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось разавторизовать сотрудника', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
}));
